import { Progress } from 'antd'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import logo from '../images/logo_vertical_yellow_black.svg'

const API = process.env.REACT_APP_API_HOST
const OTP_KEY = 'victor_otp'
const TOKEN_KEY = 'victor_platform'
export const REMEMBER_KEY = 'victor_platform_remember'

const JSON_CONTENT = { 'Content-Type': 'application/json' }

type GroupNames = 'admin' | 'client' | 'bank'

export type ActiveUser = {
  uuid: string
  first_name: string
  last_name: string
  email_address: string
  phone_number: string
  roles: Record<string, string>
  has_approval_pin: boolean
  last_login_time: number
  group: GroupNames
  organization_uuid: string
}

export type AuthTokens = {
  access: string
  refresh: string
}

export type AuthContextType = {
  activeUser: ActiveUser | undefined
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  logout: () => Promise<void>
  tokens: () => AuthTokens
  refreshTokens: () => Promise<boolean>
  verifyOtp: (otp: string) => Promise<void>
  resendOtp: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  verifyResetToken: (uuid: string, token: string) => Promise<void>
  resetPassword: (password: string, uuid: string, token: string) => Promise<void>
  sendSetPasswordOtp: (uuid: string, token: string) => Promise<void>
  setPassword: (password: string, otp: string, uuid: string, token: string) => Promise<void>
  refreshUser: () => void
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

export { AuthContext }

export function useAuth(): AuthContextType {
  const context = React.useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth() is only available within an AuthContext.Provider')
  }

  return context
}

function AuthProvider(props: React.PropsWithChildren<{}>): ReactElement {
  const { push } = useHistory()
  const [loading, setLoading] = useState(true)
  const [progress, setProgress] = useState(0)
  const [activeUser, setActiveUser] = useState<ActiveUser>()

  const getUserInfo = useCallback(async (): Promise<ActiveUser> => {
    const { access } = getTokens()
    const request = fetch(`${process.env.REACT_APP_API_HOST}/client/profile`, {
      headers: { Authorization: `Token ${access}` },
    })

    return await fetchJson(request)
  }, [])

  const login = useCallback(async (email: string, password: string, remember = false) => {
    const request = fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: JSON_CONTENT,
      body: JSON.stringify({ email, password }),
    })

    const { token } = await fetchJson(request)
    sessionStorage.setItem(OTP_KEY, token)

    if (remember) {
      localStorage.setItem(REMEMBER_KEY, 'true')
    } else {
      localStorage.removeItem(REMEMBER_KEY)
    }
  }, [])

  const logout = useCallback(async () => {
    const { access } = getTokens()
    sessionStorage.clear()
    localStorage.removeItem(TOKEN_KEY)
    setActiveUser(undefined)

    try {
      await fetch(`${API}/auth/logout`, { headers: { Authorization: `Token ${access}` } })
    } catch (e) {
      console.error('logout error', e)
    }
  }, [])

  const refreshTokens = useCallback(async () => {
    const { refresh } = getTokens()
    if (!refresh) {
      throw new Error('No active session')
    }

    const parts = refresh.split('.')

    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]))

      if (payload && payload.exp) {
        const expires = new Date(payload.exp * 1000)

        if (new Date() < expires) {
          try {
            const request = fetch(`${process.env.REACT_APP_API_HOST}/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: refresh }),
            })

            const { access_token, refresh_token } = await fetchJson(request)
            storeTokens({ access: access_token, refresh: refresh_token })
            return true
          } catch (e) {
            return false
          }
        }
      }
    }

    return false
  }, [])

  const verifyOtp = useCallback(
    async (otp: string) => {
      const token = sessionStorage.getItem(OTP_KEY)

      if (!token) {
        throw new Error('No active login present')
      }

      const request = fetch(`${API}/auth/validate-otp`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ token, otp }),
      })

      const { access_token, refresh_token, first_login } = await fetchJson(request)
      storeTokens({ access: access_token, refresh: refresh_token })

      const user = await fetchJson(
        fetch(`${API}/client/profile`, { headers: { Authorization: `Token ${access_token}` } })
      )

      setActiveUser(user)

      if (first_login && first_login === true) {
        push('/settings/change-password')
      }
    },
    [push]
  )

  const resendOtp = useCallback(async () => {
    const token = sessionStorage.getItem(OTP_KEY)

    if (!token) {
      throw new Error('No active login present')
    }

    await fetchJson(
      fetch(`${API}/auth/resend-otp`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ token }),
      })
    )
  }, [])

  const forgotPassword = useCallback(async (email: string) => {
    await fetchJson(
      fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ email }),
      })
    )
  }, [])

  const verifyResetToken = useCallback(async (uuid: string, token: string) => {
    await fetchJson(
      fetch(`${API}/auth/verify-resettoken`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ uuid, reset_token: token }),
      })
    )
  }, [])

  const resetPassword = useCallback(async (password: string, uuid: string, token: string) => {
    await fetchJson(
      fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ password, uuid, reset_token: token }),
      })
    )
  }, [])

  const sendSetPasswordOtp = useCallback(async (uuid: string, token: string) => {
    await fetchJson(
      fetch(`${API}/auth/password-otp`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ uuid, reset_token: token }),
      })
    )
  }, [])

  const setPassword = useCallback(async (password: string, otp: string, uuid: string, token: string) => {
    await fetchJson(
      fetch(`${API}/auth/set-password`, {
        method: 'POST',
        headers: JSON_CONTENT,
        body: JSON.stringify({ password, otp, uuid, reset_token: token }),
      })
    )
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      setActiveUser(await getUserInfo())
    } catch (e) {
      // one retry attempt
      try {
        if (await refreshTokens()) {
          setActiveUser(await getUserInfo())
        } else {
          await logout()
        }
      } catch (e) {
        await logout()
      }
    }
  }, [getUserInfo, refreshTokens, logout])

  useEffect(() => {
    const loaderInterval = setInterval(() => setProgress((prev) => (prev >= 100 ? 0 : prev + 10)), 500)
    ;(async function () {
      try {
        if (await refreshTokens()) {
          const user = await getUserInfo()
          setActiveUser(user)
        }
      } catch (e) {
        // noop -- require login
      }

      clearInterval(loaderInterval)
      setProgress(100)
      setLoading(false)
    })()
  }, [refreshTokens, getUserInfo])

  useEffect(() => {
    const onUnload = () => {
      if (!JSON.parse(localStorage.getItem(REMEMBER_KEY) ?? 'false')) {
        localStorage.removeItem(TOKEN_KEY)
      }
    }

    window.addEventListener('unload', onUnload)
    return () => window.removeEventListener('unload', onUnload)
  }, [])

  const authContextValue = useMemo(() => {
    return {
      activeUser,
      login,
      logout,
      tokens: getTokens,
      refreshTokens,
      verifyOtp,
      resendOtp,
      forgotPassword,
      verifyResetToken,
      resetPassword,
      sendSetPasswordOtp,
      setPassword,
      refreshUser,
    }
  }, [
    activeUser,
    login,
    logout,
    refreshTokens,
    verifyOtp,
    resendOtp,
    forgotPassword,
    verifyResetToken,
    resetPassword,
    sendSetPasswordOtp,
    setPassword,
    refreshUser,
  ])

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <img src={logo} width={250} alt="Victor" />
        <Progress percent={progress} showInfo={false} style={{ width: 250 }} strokeColor="#F7BE00" />
      </div>
    )
  }

  return <AuthContext.Provider value={authContextValue} {...props} />
}

export default AuthProvider

function storeTokens(tokens: AuthTokens): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
}

function getTokens(): AuthTokens {
  const tokenString = localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY)
  return tokenString ? (JSON.parse(tokenString) as AuthTokens) : ({} as any)
}

async function fetchJson(fetchPromise: Promise<Response>) {
  const response = await fetchPromise
  let body
  try {
    body = await response.json()
  } catch (e) {
    // no-op
  }

  if (response.status === 200) {
    if (body.status === 1) {
      return body.data
    } else if (body.error) {
      throw new Error(body.error)
    }
  }

  throw body.error ? body.error : new Error(`${response.status} unsuccessful`)
}
